import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { Device } from '../../types/api';
import type { DeploymentKind } from '@store/api/deploymentsApi';
import { compareVersions, parseVersion } from '../../utils/version';

const ALL = '__all__';
const UNKNOWN = '__unknown__';
const ONLINE_STATUSES = ['online', 'away'];

/** The Device's installed version of this kind of Update Artifact. OS packages
 *  have none. */
const deviceVersion = (device: Device, kind: DeploymentKind) =>
  ({
    client: device.clientVersion,
    dotnet: device.comnovoGatewayVersion,
    dotnet_runtime: device.dotnetRuntimeVersion,
    os_packages: undefined,
  })[kind];

const VERSION_LABEL: Record<DeploymentKind, string | null> = {
  client: 'Client',
  dotnet: 'Comnovo',
  dotnet_runtime: '.NET runtime',
  os_packages: null,
};

interface DeviceTargetPickerProps {
  kind: DeploymentKind;
  /** The version being deployed; offers "only devices below it". */
  targetVersion: string;
  devices: Device[];
  selected: string[];
  onChange: (deviceIds: string[]) => void;
  disabled?: boolean;
}

/** Choose verified Devices by search and filters. Only what is ticked is targeted. */
const DeviceTargetPicker: React.FC<DeviceTargetPickerProps> = ({
  kind,
  targetVersion,
  devices,
  selected,
  onChange,
  disabled = false,
}) => {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState(ALL);
  const [version, setVersion] = useState(ALL);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [belowTarget, setBelowTarget] = useState(false);

  // Unverified devices cannot receive a Deployment; the server rejects them.
  const verified = useMemo(
    () => devices.filter((d) => d.registrationStatus === 'verified'),
    [devices]
  );
  const groups = useMemo(
    () => [...new Set(verified.map((d) => d.groupName).filter(Boolean))].sort() as string[],
    [verified]
  );
  const versions = useMemo(
    () =>
      [...new Set(verified.map((d) => deviceVersion(d, kind)).filter(Boolean))].sort() as string[],
    [verified, kind]
  );

  const matching = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const target = parseVersion(targetVersion);
    return verified.filter((d) => {
      const installed = deviceVersion(d, kind);
      if (
        needle &&
        ![d.deviceId, d.name, d.customerName].some((v) => v?.toLowerCase().includes(needle))
      ) {
        return false;
      }
      if (group !== ALL && d.groupName !== group) return false;
      if (version === UNKNOWN ? installed : version !== ALL && installed !== version) return false;
      if (onlineOnly && !ONLINE_STATUSES.includes(d.status)) return false;
      if (belowTarget && target) {
        const parsed = parseVersion(installed);
        // Missing or unparseable versions are not known to be current.
        if (parsed && compareVersions(parsed, target) >= 0) return false;
      }
      return true;
    });
  }, [verified, kind, search, group, version, onlineOnly, belowTarget, targetVersion]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const versionLabel = VERSION_LABEL[kind];

  const toggle = (deviceId: string) =>
    onChange(
      selectedSet.has(deviceId) ? selected.filter((id) => id !== deviceId) : [...selected, deviceId]
    );
  const selectMatching = () =>
    onChange([...new Set([...selected, ...matching.map((d) => d.deviceId)])]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          label="Search devices"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          sx={{ flex: '1 1 200px' }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
          <InputLabel id="picker-group-label">Group</InputLabel>
          <Select
            labelId="picker-group-label"
            label="Group"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
          >
            <MenuItem value={ALL}>All groups</MenuItem>
            {groups.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {versionLabel && (
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
            <InputLabel id="picker-version-label">{versionLabel} version</InputLabel>
            <Select
              labelId="picker-version-label"
              label={`${versionLabel} version`}
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            >
              <MenuItem value={ALL}>All versions</MenuItem>
              {versions.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
              <MenuItem value={UNKNOWN}>Unknown</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={onlineOnly}
              onChange={(e) => setOnlineOnly(e.target.checked)}
              disabled={disabled}
            />
          }
          label="Online only"
        />
        {versionLabel && (
          <FormControlLabel
            control={
              <Checkbox
                checked={belowTarget}
                onChange={(e) => setBelowTarget(e.target.checked)}
                disabled={disabled}
              />
            }
            label={`Only below ${targetVersion}`}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          {selected.length} selected
        </Typography>
        <Button size="small" onClick={selectMatching} disabled={disabled || matching.length === 0}>
          Select {matching.length} shown
        </Button>
        <Button size="small" onClick={() => onChange([])} disabled={disabled || selected.length === 0}>
          Clear
        </Button>
      </Box>
      <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto', px: 2, py: 1 }}>
        {matching.length === 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              py: 1
            }}>
            No verified devices match these filters.
          </Typography>
        )}
        {matching.map((device) => (
          <FormControlLabel
            key={device.deviceId}
            sx={{ display: 'flex' }}
            control={
              <Checkbox
                checked={selectedSet.has(device.deviceId)}
                onChange={() => toggle(device.deviceId)}
                disabled={disabled}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{device.name || device.deviceId}</Typography>
                <Typography variant="caption" sx={{
                  color: 'text.secondary'
                }}>
                  Status: {device.status} |{' '}
                  {versionLabel
                    ? `${versionLabel}: ${deviceVersion(device, kind) || 'Unknown'}`
                    : `Pending updates: ${device.osUpdatesPending ?? 'Unknown'}`}
                  {device.groupName ? ` | Group: ${device.groupName}` : ''}
                </Typography>
              </Box>
            }
          />
        ))}
      </Paper>
    </Box>
  );
};

export default DeviceTargetPicker;
